import { useMemo, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import AxiosInstance from "@/utils/AxiosInstance";
import { getPromise, getPromiseToast } from "@/utils/helpers";
import { Input } from "./ui/input";

export default function EnrollNotesConsentForm({
  patient,
  onClose,
  onEnrollSuccess,
}: {
  patient: any;
  onClose: () => void;
  onEnrollSuccess?: () => void;
}) {
  const [notes, setNotes] = useState("");
  const [consentFile, setConsentFile] = useState<File | null>(null);
  const [zendeskTicketNumber, setZendeskTicketNumber] = useState("");
  const [consentChecks, setConsentChecks] = useState<Record<string, boolean>>({
    // copay: false,
    // refuse: false,
    // planShared: false,
    // oneProvider: false,
    informedConsent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const consentVerifiedAt = useMemo(() => new Date(), []);
  const consentVerifiedAtDisplay = useMemo(
    () => consentVerifiedAt.toLocaleString(),
    [consentVerifiedAt],
  );

  const toggleConsent = (key: string) => {
    setConsentChecks((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const allConsentChecked = useMemo(
    () => Object.values(consentChecks).every(Boolean),
    [consentChecks],
  );

  const handleSubmit = async () => {
    if (!notes?.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!zendeskTicketNumber?.trim()) {
      toast.error("Please enter Consent Proof");
      return;
    }
    // if (!consentFile) {
    //   toast.error("Please upload consent proof (audio/photo/video)");
    //   return;
    // }
    // if (!allConsentChecked) {
    //   toast.error("Please verify all consent checkboxes");
    //   return;
    // }
    if (!patient?._id) {
      toast.error("Patient information is missing");
      return;
    }

    setIsSubmitting(true);
    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);

    try {
      // const formData = new FormData();
      // formData.append("patientId", patient._id);
      // formData.append("notes", notes.trim());
      // // Keep backward-compat with existing required field name
      // formData.append("zendeskTicketNumber", consentFile?.name || "consent");
      // formData.append("consentVerifiedAt", consentVerifiedAt.toISOString());
      // formData.append("consentChecklist", JSON.stringify(consentChecks));
      // formData.append("consentFile", consentFile);
      // for (const [key, value] of formData.entries()) {
      //   console.log(key, value);
      // }

      const payload = {
        patientId: patient._id,
        notes: notes.trim(),
        // zendeskTicketNumber: zendeskTicketNumber.trim(),
        consentChecklist: consentChecks,
        zendeskTicketNumber: zendeskTicketNumber.trim(),
      };
      // console.log("payload", payload);
      // return;

      await AxiosInstance().post("/patients/enroll", payload);

      // await AxiosInstance().post("/patients/enroll", formData, {
      //   headers: { "Content-Type": "multipart/form-data" },
      // });

      resolveFn("Patient enrolled successfully");
      onClose();
      setNotes("");
      setConsentFile(null);
      setConsentChecks({
        copay: false,
        refuse: false,
        planShared: false,
        oneProvider: false,
      });
      if (onEnrollSuccess) onEnrollSuccess();
    } catch (error: any) {
      rejectFn(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to enroll patient",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Enrollment Note */}
      <div className="space-y-2">
        <Label htmlFor="enroll-notes">Enrollment Note*</Label>
        <Textarea
          id="enroll-notes"
          placeholder="Enrollment note..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="min-h-[100px] resize-none"
          required
        />
      </div>

      {/* Enter Zendesk Ticket Number */}
      <div className="space-y-2">
        <Label htmlFor="zendesk-ticket">Enter Consent Proof*</Label>
        <Input
          id="zendesk-ticket"
          placeholder="Consent Proof"
          value={zendeskTicketNumber}
          onChange={(e) => setZendeskTicketNumber(e.target.value)}
          required
        />
      </div>

      {/* Date & time */}
      {/* <div className="space-y-2">
        <Label htmlFor="enroll-datetime">Date &amp; time</Label>
        <Input
          id="enroll-datetime"
          value={consentVerifiedAtDisplay}
          disabled
          className="bg-muted"
        />
      </div> */}

      {/* Consent proof upload */}
      {/* <div className="space-y-2">
        <Label htmlFor="consent-upload">Consent (Audio file / Photo / Video)*</Label>
        <Input
          id="consent-upload"
          type="file"
          accept="audio/*,image/*,video/*"
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            setConsentFile(f);
          }}
          required
        />
        {consentFile ? (
          <div className="text-xs text-muted-foreground break-all">
            Selected: {consentFile?.name || ""}
          </div>
        ) : null}
      </div> */}

      {/* Consent checkboxes (from screenshot) */}
      <div className="space-y-2 rounded-md border p-3">
        <div className="text-sm text-muted-foreground">
          {/* <span className="text-red-500 font-bold">*</span> */}
           Please verify that the
          patient has been informed verbally of the following information:
        </div>

        <div className="space-y-2 pt-1">
          {/* <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1 accent-blue-500 cursor-pointer"
              checked={!!consentChecks.copay}
              onChange={() => toggleConsent("copay")}
            />
            <span>There could be a copay (cost-sharing)</span>
          </label>

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1 accent-blue-500 cursor-pointer"
              checked={!!consentChecks?.refuse}
              onChange={() => toggleConsent("refuse")}
            />
            <span>You have the right to refuse/revoke enrollment at any time</span>
          </label>

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1 accent-blue-500 cursor-pointer"
              checked={!!consentChecks?.planShared}
              onChange={() => toggleConsent("planShared")}
            />
            <span>Your Plan of Care can be shared with your other providers</span>
          </label>

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1 accent-blue-500 cursor-pointer"
              checked={!!consentChecks?.oneProvider}
              onChange={() => toggleConsent("oneProvider")}
            />
            <span>
              Only one provider per month can provide these services to you
            </span>
          </label> */}

<label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1 accent-blue-500 cursor-pointer"
              checked={!!consentChecks?.informedConsent}
              onChange={() => toggleConsent("informedConsent")}
            />
            <span>I hereby provide my informed consent to enroll in care management services offered by Anthro Care On behalf of my primary care provider. I understand that these services involve the collection and monitoring of my health data, ongoing care coordination, and communication with healthcare providers to support the management of my condition(s). I acknowledge that participation is voluntary, and I may opt out at any time by informing the care team. I authorize the use and disclosure of my health information as necessary to deliver these services in accordance with applicable laws and regulations.</span>
          </label>
        </div>
      </div>

      {/* Confirm Button */}
      <div className="flex justify-end pt-2 sticky bottom-0 w-full bg-white">
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || !notes?.trim() }
        >
          {isSubmitting ? "Submitting..." : "Confirm"}
        </Button>
      </div>
    </div>
  );
}

